import React, { useState, useEffect } from 'react';
import BackSpace from '@material-ui/icons/Backspace';
import Tooltip from '@material-ui/core/Tooltip';
import { FileSelectDialog } from '../../../common/SharedComponents';
import { ApplyFileStatusType } from '../../common/ApplicationConstants';
import { handleAdminSuccess, handleAdminError, handleAdminWarning } from '../../../common/SharedFunctions';
import { FileGenerationStatusType } from '@apply/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faDownload, faGears } from '@fortawesome/free-solid-svg-icons';

const reviewStyles = {
    buttonMarginRight: { marginRight: '10px' },
    hrDefault: { marginTop: '0' },
    nonEditable: {
        backgroundColor: '#f7f7f7',
        padding: '5px 10px',
        margin: '5px',
    },
    noPaddingRight: { paddingLeft: '0' },
    noPaddingLeft: { paddingLeft: '0' },
    margin10Bottom: { marginBottom: '10px' },
    leftLabel: { paddingLeft: '0', paddingTop: '8px', paddingRight: '0' },
    iconContainer: { paddingTop: '8px', paddingLeft: '0', paddingRight: '0' },
    paidContainer: { paddingTop: '8px', marginBottom: '1rem' },
    statusMsgContainer: {
        paddingTop: '8px',
        paddingLeft: '10px',
        paddingRight: '0',
    },
    statusMsgContainer2: {
        paddingTop: '20px',
        paddingLeft: '10px',
        paddingRight: '0',
    },
    iconYes: { color: 'mediumblue', cursor: 'pointer' },
    iconNo: { color: 'silver', cursor: 'not-allowed' },
    iconCancel: { color: 'darkred', cursor: 'pointer' },
    buttonContainer: { marginRight: '20px', textAlign: 'right' },
    blue: { color: 'blue' },
    green: { color: 'green' },
    red: { color: 'red' },
    black: { color: 'black' },
    fileRow: { height: '45px' },
};

export const FileList = ({
    parentId,
    files,
    onFileChange,
    disabled = false,
    onFileRegenerate,
    onFileStatusUpdate,
    readonly = false,
}) => {
    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const _setFileStatus = (e, index) => {
        const file = files[index];
        if (!file) {
            return;
        }

        var statusId = parseInt(e.target.value);
        if (statusId === ApplyFileStatusType.NotApplicable) {
            const options = {
                ownerId: file.id,
                isOpen: true,
                maxFiles: 1,
                isReplace: false,
                fileTypeName: file.fileTypeName,
            };
            set_fileSelectOptions(options);
            return;
        }

        file.statusId = statusId;
        onFileChange && onFileChange(file, parentId);
    };

    const onFileSelectSubmit = (id, filesToUpload) => {
        if (!id) {
            return;
        }

        if (!filesToUpload || filesToUpload.length === 0) {
            _onRemoveTempFile(id);
            set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
            return;
        }

        const fileToUpdate = files.find((f) => f.id === id);
        if (!fileToUpdate) {
            return;
        }

        fileToUpdate.file = filesToUpload[0];
        fileToUpdate.oldStatusId = fileToUpdate.statusId;
        fileToUpdate.statusId = ApplyFileStatusType.Approved;
        onFileChange && onFileChange(fileToUpdate, parentId);
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const _onRemoveTempFile = (id) => {
        if (!id) {
            return;
        }

        const fileToUpdate = files.find((f) => f.id === id);
        if (!fileToUpdate) {
            return;
        }

        fileToUpdate.file = null;
        fileToUpdate.statusId = fileToUpdate.oldStatusId ?? fileToUpdate.statusId;
        onFileChange && onFileChange(fileToUpdate, parentId);
    };

    const _getStatusStyle = (statusId) => {
        switch (statusId) {
            case 0:
                return reviewStyles.black;
            case 1:
                return reviewStyles.blue;
            case 2:
                return reviewStyles.green;
            case 3:
                return reviewStyles.red;
            default:
                return null;
        }
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onRegenerate = (e, fileId) => {
        e.preventDefault();
        e.stopPropagation();
        onFileRegenerate && onFileRegenerate(fileId);
    };

    const onRefresh = (e, fileId) => {
        e.preventDefault();
        e.stopPropagation();
        onFileStatusUpdate && onFileStatusUpdate(fileId);
    };

    if (!files || files.length === 0) {
        return <div style={{ paddingBottom: '10px' }}>No Files Found!</div>;
    }

    return (
        <>
            {files.map((file, index) => {
                return (
                    <div
                        className='row'
                        key={file.id}
                        style={reviewStyles.fileRow}
                    >
                        <div
                            className='columns small-12 large-2'
                            style={reviewStyles.leftLabel}
                        >
                            {file.fileTypeName}
                        </div>
                        <div className='columns small-12 large-3'>
                            {file.statuses && (
                                <select
                                    value={file.statusId ?? 0}
                                    onChange={(e) => _setFileStatus(e, index)}
                                    required
                                    style={_getStatusStyle(file.statusId)}
                                    disabled={disabled}
                                >
                                    {file.statuses &&
                                        file.statuses.map((fs) => {
                                            if (file.statusId > 0 && fs.id === '0') {
                                                return null;
                                            } else {
                                                return (
                                                    <option
                                                        key={fs.id}
                                                        value={fs.id}
                                                        style={reviewStyles.black}
                                                    >
                                                        {fs.name}
                                                    </option>
                                                );
                                            }
                                        })}
                                </select>
                            )}
                            {file.createdDate && (
                                <div
                                    className='columns small-12 large-4'
                                    style={reviewStyles.leftLabel}
                                >
                                    {new Date(file.createdDate).toLocaleDateString('en-us')}
                                </div>
                            )}
                        </div>
                        <div
                            className='columns small-12 large-1'
                            style={reviewStyles.iconContainer}
                        >
                            <Tooltip title='Download file'>
                                <a
                                    href={file.url}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    <FontAwesomeIcon
                                        icon={faDownload}
                                        style={file.url ? reviewStyles.iconYes : reviewStyles.iconNo}
                                    />
                                </a>
                            </Tooltip>
                            &nbsp;
                            {file.generationStatusId !== FileGenerationStatusType.NotApplicable && !readonly && (
                                <>
                                    &nbsp;
                                    <button
                                        className='link'
                                        title={
                                            file.generationStatusId === FileGenerationStatusType.ToBeGenerated
                                                ? 'Regenerating File. Click to refresh status...'
                                                : 'Click to request PDF regeneration.'
                                        }
                                        onClick={(e) =>
                                            file.generationStatusId === FileGenerationStatusType.ToBeGenerated
                                                ? onRefresh(e, file.id)
                                                : onRegenerate(e, file.id)
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={
                                                file.generationStatusId === FileGenerationStatusType.ToBeGenerated
                                                    ? faRepeat
                                                    : faGears
                                            }
                                            style={
                                                file.generationStatusId === FileGenerationStatusType.ToBeGenerated
                                                    ? { color: 'green' }
                                                    : { color: 'darkred' }
                                            }
                                        />
                                    </button>
                                    &nbsp;
                                </>
                            )}
                            {file.file && !readonly && (
                                <Tooltip title='Cancel File upload/replace'>
                                    <BackSpace
                                        style={reviewStyles.iconCancel}
                                        onClick={() => {
                                            _onRemoveTempFile(file.id);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <div
                            className='columns small-12 large-4'
                            style={reviewStyles.statusMsgContainer}
                        >
                            {file?.statusChangedByMsg && file.statusChangedByMsg}
                        </div>
                    </div>
                );
            })}
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
        </>
    );
};
